/**
 * Input Controller
 * Manages input area including textarea, submit button, disabled overlay, and autocomplete
 */

import { escapeHtml, eventBus } from '../core/utils.js';
import { getFilteredWords } from '../services/AutocompleteService.js';
import { renderMenuItems } from '../components/optionsMenu.js';
import { buildActionsHelpPanel } from '../chat/helpHintPanel.js';
import { resolveAdviceCreateMenuItems } from '../core/adviceCreateMenu.js';
import { Tooltip } from '../utils/Tooltip.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';
import {
  ACCEPTED_IMAGE_TYPES,
  CONVERTIBLE_EXTENSIONS,
  CONVERTIBLE_IMAGE_TYPES,
  IMAGE_QUALITY,
  MAX_IMAGE_DIMENSION,
  MAX_IMAGE_SIZE_MB,
  MAX_IMAGES,
  extensionForType,
  pickerAcceptAttr
} from '../constants/attachments.js';

/** Type that convertible images (HEIC/HEIF) are re-encoded to. */
const CONVERSION_OUTPUT_TYPE = 'image/jpeg';

/**
 * Shown when the browser cannot decode HEIC. Names the way out, because the
 * user cannot tell from the failure that their browser is the problem.
 */
const HEIC_UNSUPPORTED_MESSAGE =
  "HEIC photos aren't supported";

/** The remedy, shown as the toast's second line. Split from the message above
 *  so the cause reads first and the fix does not push it past the fold. */
const HEIC_UNSUPPORTED_DETAIL =
  'Open the photo and export it as JPEG, or use the Safari browser.';

/** @constant {number} Debounce delay for autocomplete search (ms) */
const AUTOCOMPLETE_DEBOUNCE_MS = 100;

/** @constant {number} Debounce delay for composer draft persistence (ms) */
const DRAFT_DEBOUNCE_MS = 500;

/**
 * @constant {number} Slack (px) above the captured one-row textarea height that
 * counts as "content wrapped past the first line" — the threshold for toggling the
 * two-row expand ({@link Input#autoExpand}). A wrap adds a full line-height
 * (~26px at the 16px relaxed line), so a small slack cleanly separates one line from
 * two without tripping on sub-pixel measurement noise.
 */
const SINGLE_ROW_EXPAND_SLACK_PX = 6;

/** @constant {string} Shared mobile breakpoint for homepage placeholder copy. */
const MOBILE_PLACEHOLDER_MEDIA_QUERY = '(max-width: 640px)';

/**
 * @constant {number} Number of bars in the recording waveform meter. Even so the
 * two center bars share the newest sample and left/right mirror perfectly. The
 * bars are generated in {@link Input#_buildWaveformBars}; the history buffer holds
 * `WAVEFORM_BAR_COUNT / 2` samples (one per side, center → edge).
 */
const WAVEFORM_BAR_COUNT = 64;

/**
 * @constant {number} How often (ms) the amplitude history shifts one step outward
 * toward the edges. Larger = a slower-travelling ripple. The center bar still tracks
 * live amplitude every frame; only the outward propagation is throttled to this
 * cadence (travel time center→edge ≈ (WAVEFORM_BAR_COUNT/2) × this).
 */
const WAVEFORM_SHIFT_MS = 55;

/**
 * @constant {number} Resting scaleY for a silent/edge bar (≈6px at the 40px bar
 * height). Matches the CSS default (`.chat-input__waveform-bar`) so the idle meter
 * shows a row of short bars rather than collapsing to an invisible line.
 */
const WAVEFORM_MIN_SCALE = 0.15;

/**
 * @constant {number} Input gain applied to the raw mic RMS before the response
 * curve. >1 = more sensitive (bigger waves at lower volume). The product is clamped
 * to 1, so a high gain makes normal speech saturate the meter.
 */
const WAVEFORM_GAIN = 3;

/**
 * @constant {number} Response-curve exponent (gamma) for the amplitude. <1 is
 * compressive — it lifts quiet input toward full height while loud input still
 * saturates at 1; 1 = linear. Lower = even more sensitive to soft speech.
 */
const WAVEFORM_CURVE = 0.6;

/**
 * @constant {number} Fraction of the half-width (center → edge) over which the
 * envelope fades to zero. The outer `1 - this` of the bars stay at their resting
 * height — the wave dies out before reaching the edges. 0.86 = outer 14% never move.
 */
const WAVEFORM_EDGE_FADE = 0.86;

export class Input {
  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.chatInput - Textarea element
   * @param {HTMLElement} elements.submitButton - Conditional submit (send / stop-generation) button
   * @param {HTMLElement} elements.micButton - Persistent mic / stop-recording / processing button
   * @param {HTMLElement} elements.inputDisabledOverlay - Disabled overlay element
   * @param {HTMLElement} elements.autocompleteContainer - Autocomplete dropdown container
   * @param {HTMLElement} elements.autocompleteList - Autocomplete list element
   * @param {HTMLElement} elements.plusButton - Plus button element
   * @param {EventBus} eventBus - Event bus for module communication
   * @param {Object} [autocompleteService] - Optional autocomplete service for suggestions
   */
  constructor(elements, eventBus, autocompleteService = null) {
    this.chatInput = elements.chatInput;
    this.submitButton = elements.submitButton;
    this.micButton = elements.micButton;
    this.inputDisabledOverlay = elements.inputDisabledOverlay;
    this.autocompleteContainer = elements.autocompleteContainer;
    this.autocompleteList = elements.autocompleteList;
    this.plusButton = elements.plusButton;
    this.eventBus = eventBus;
    this.autocompleteService = autocompleteService;

    // Get the main container for state classes
    this.container = this.chatInput?.closest('.chat-input');

    // In-input action-button region (generic relocation target). Self-queried so
    // no explicit app.js element-ref wiring is required.
    this.buttonsEl = document.getElementById('input-buttons');
    this.buttonsSlot = document.getElementById('input-buttons-slot');
    this.textFallbackBtn = document.getElementById('input-text-fallback');
    this._activeButtonGroupId = null; // groupId of the group currently mounted, or null
    this._stashedButtons = null;      // group captured when the user chose the text fallback
    // True while a typed detour is in progress over a stashed DURABLE-proposal
    // group (app-tools plan Phase 3.5, decision #8): the stash itself survives as
    // a state carrier, but the "Back to buttons" pill hides for the detour's
    // duration so the input looks completely normal. Always false when
    // `_stashedButtons` is null (cleared together in `_clearStashedButtons`).
    this._stashPillSuppressed = false;
    // Markup default for the text-fallback link, captured once so per-group
    // `textEntryLabel` overrides (see showButtons) can be reverted on the next mount.
    this._defaultTextFallbackLabel = this.textFallbackBtn?.textContent?.trim()
      || 'Or, click to respond in your own words';

    // State
    this.isSubmitting = false;
    this._submitStartedAt = null;
    this.chatBusy = false; // Programmatic chat activity (see chat:busy listener)
    this.autocompleteVisible = false;
    this.debounceTimer = null;
    this.draftDebounceTimer = null;
    this.currentDraftSessionId = null;
    this.blurHideTimeout = null; // Timeout for delayed autocomplete hide on blur
    this.selectedIndex = -1; // Keyboard navigation index (-1 = none)
    this.currentMode = null; // Track mode for tooltip text (null = on dashboard)
    this.hasSubmittedFirstMessage = false; // Gates autocomplete only: suggestions show until the conversation's first submit; reopens on mode:cleared (new chat), stays closed on mode:changed/mode:restored
    this.customPlaceholder = false; // Track if mode set a custom placeholder (prevents auto-reset)
    this.pendingMode = null; // Pre-selected mode for dashboard (not yet switched)
    this.chatManager = null; // ChatManager reference for stop functionality
    this.currentLayout = 'full'; // Feature toggle layout state: 'simple' | 'full'
    this.plusToggleDisabled = false; // Track if plus button is disabled by feature toggle
    this.audioService = null; // AudioRecordingService instance
    this.audioState = 'idle'; // Track audio state: 'idle' | 'recording' | 'processing'

    // Recording waveform UI (replaces the textarea while recording/transcribing).
    this.waveformEl = document.getElementById('chat-input-waveform');
    this.waveformLabel = document.getElementById('chat-input-waveform-label');
    this.srStatus = document.getElementById('chat-input-sr-status');
    this._waveformRaf = null;
    // Amplitude history, one sample per side (center → edge); rippled outward each
    // WAVEFORM_SHIFT_MS. Generates the bars + sizes the buffer.
    this._waveHistory = new Array(WAVEFORM_BAR_COUNT / 2).fill(0);
    this._lastWaveShift = 0;
    this.waveformBars = [];
    this._buildWaveformBars();
    this._singleRowHeight = 0; // captured one-row textarea height for resetInputHeight()

    // Image attachment state
    this.attachedImages = []; // Array of { id, file, preview, base64, mimeType, filename }
    this.maxImages = MAX_IMAGES;
    this.maxImageSizeMB = MAX_IMAGE_SIZE_MB;
    this.allowedTypes = ACCEPTED_IMAGE_TYPES;
    this._imageIngestionQueue = Promise.resolve();
    // Ingestion lifecycle. Reading, decoding and re-encoding an image is async,
    // so work started before a submit/clear can finish after it. The generation
    // counter invalidates that stale work (see _isIngestionStale) and the
    // pending count keeps send from racing it.
    this._ingestionGeneration = 0;
    this._pendingIngestions = 0;
    this.fileInput = this._createFileInput();

    // Plus dropdown state
    this.plusDropdown = document.getElementById('plus-dropdown');
    this.plusDropdownCaptureLabel = document.getElementById('plus-dropdown-capture-label');
    this.plusDropdownOpen = false;
    this._handlePlusOutsideClick = this._handlePlusOutsideClick.bind(this);
    this._handlePickerOutsideClick = this._handlePickerOutsideClick.bind(this);
    this._pickerDismissActive = false;

    // Create menu ("Actions" icon → build a practice / write a note). Membership is
    // the backend `adviceCreateMenu` projection, never derived here; `null` means
    // the control does not exist for this turn. See setCreateMenu.
    this.createContainer = document.getElementById('create-menu-container');
    this.createButton = document.getElementById('create-menu-button');
    this.createMenu = document.getElementById('create-menu');
    this.createMenuOpen = false;
    this.createMenuItems = null;   // descriptor item keys, or null
    this._renderedCreateItems = ''; // last rendered item signature (render dedupe)
    this._handleCreateOutsideClick = this._handleCreateOutsideClick.bind(this);

    // Feature detection for screen capture
    this.hasScreenCapture = !!navigator.mediaDevices?.getDisplayMedia;

    // Camera file input for mobile
    this.cameraInput = this._createCameraInput();

    // Placeholder text constants
    this.PLACEHOLDER_INITIAL = 'Share what\'s on your mind...';
    this.PLACEHOLDER_INITIAL_MOBILE = 'Share your mind...';
    this.PLACEHOLDER_REPLY = 'Reply...';
    this._placeholderMediaQuery = window.matchMedia?.(MOBILE_PLACEHOLDER_MEDIA_QUERY) || null;
    this._handlePlaceholderBreakpointChange = () => this.updatePlaceholder();
    this._placeholderMediaQuery?.addEventListener?.(
      'change',
      this._handlePlaceholderBreakpointChange
    );

    this.initialize();
  }

  /**
   * Initialize input area functionality
   */
  initialize() {
    // Auto-expand textarea
    if (this.chatInput) {
      this.chatInput.addEventListener('input', () => {
        this.handleChange();
        this.autoExpand();
        this.handleAutocompleteInput();
        this._scheduleDraftSave();
        // Stop attention pulse when user starts typing
        this.stopAttentionPulse();
      });

      // Treat pasted image files like picker attachments while preserving the
      // browser's native behavior for ordinary text paste.
      this.chatInput.addEventListener('paste', (e) => this._handlePaste(e));
      this._setupDragAndDrop();

      // Handle keyboard navigation
      this.chatInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          // If autocomplete is open and item is selected, submit that item
          if (this.autocompleteVisible && this.selectedIndex >= 0) {
            this.submitSelectedAutocomplete();
          } else {
            this.hideAutocomplete();
            if (!this._isLocked() && (this.getValue().trim() || this.attachedImages.length > 0)) {
              this.handleSubmit();
            }
          }
        } else if (e.key === 'Escape') {
          this.hideAutocomplete();
        } else if (e.key === 'ArrowDown' && this.autocompleteVisible) {
          e.preventDefault();
          this.navigateAutocomplete(1);
        } else if (e.key === 'ArrowUp' && this.autocompleteVisible) {
          e.preventDefault();
          this.navigateAutocomplete(-1);
        }
      });

      // Focus/blur for visual feedback
      this.chatInput.addEventListener('focus', () => this.setFocused(true));
      this.chatInput.addEventListener('blur', () => {
        this.setFocused(false);
        // Delay hiding autocomplete to allow click events to fire
        this.blurHideTimeout = setTimeout(() => this.hideAutocomplete(), 150);
      });

      // Initial setup
      // Capture the rendered rows="1" box before autosizing. Unlike scrollHeight,
      // clientHeight is not inflated when a long placeholder wraps on a narrow
      // viewport.
      this._singleRowHeight = this.chatInput.clientHeight || 0;
      this.autoExpand();

      // Extend clickable area - clicking wrapper focuses textarea
      this.chatInput.closest('.chat-input__text-wrapper')
        ?.addEventListener('click', () => this.focus());
    }

    // Submit button click - routes to appropriate handler based on state
    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.handleSubmitButtonClick());
    }

    // Mic button click - routes to start/stop recording based on mic-state
    if (this.micButton) {
      this.micButton.addEventListener('click', () => this.handleMicButtonClick());
    }

    // Text fallback ("respond in your own words") — tear down in-input buttons and
    // restore the textarea so the user can type freely.
    if (this.textFallbackBtn) {
      this.textFallbackBtn.addEventListener('click', () => this._handleTextFallbackClick());
    }

    // Plus button click — opens dropdown
    if (this.plusButton) {
      this.plusButton.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handlePlusClick();
      });
    }

    // Update capture label based on device capability
    if (this.plusDropdownCaptureLabel && !this.hasScreenCapture) {
      this.plusDropdownCaptureLabel.textContent = 'Take a photo';
    }

    // Plus dropdown item clicks
    if (this.plusDropdown) {
      this.plusDropdown.addEventListener('click', (e) => {
        const item = e.target.closest('.plus-dropdown__item');
        if (!item) return;
        this._handlePlusDropdownAction(item.dataset.action);
      });
    }

    this._initializeCreateMenu();

    // Autocomplete item clicks
    if (this.autocompleteList) {
      this.autocompleteList.addEventListener('click', (e) => {
        const item = e.target.closest('.chat-input__autocomplete-item');
        if (item) {
          this.handleAutocompleteClick(item);
        }
      });
    }

    // Listen for message completion to reset state
    if (this.eventBus) {
      this.eventBus.on('message:complete', () => {
        this.resetSubmitState();
      });

      this.eventBus.on('session:started', ({ sessionId } = {}) => {
        this.currentDraftSessionId = sessionId || null;
        this._restoreDraft();
      });
      this.eventBus.on('session:ended', () => {
        this.currentDraftSessionId = null;
        this.setCreateMenu(null);
      });

      // Programmatic chat activity (hardcoded messages, simulated streaming,
      // animation tail). ChatManager emits chat:busy/chat:idle via a
      // refcount so overlapping programmatic messages keep the input locked
      // until the last one finishes animating. `isSubmitting` is reserved
      // for "user dispatched a real LLM request" — this flag tracks the
      // broader "chat is busy" state and is OR-ed with isSubmitting via
      // _isLocked() at every submit gate.
      this.eventBus.on('chat:busy', () => {
        this.chatBusy = true;
        this.updateButtonState();
      });
      this.eventBus.on('chat:idle', () => {
        this.chatBusy = false;
        this.updateButtonState();
      });

      // Track mode changes for placeholder text. Deliberately does NOT touch
      // hasSubmittedFirstMessage: the first dashboard submit is itself what
      // creates the mode, so a reset here reopened autocomplete for the
      // second message. The flag only reopens on mode:cleared (new chat).
      this.eventBus.on('mode:changed', (data) => {
        this.currentMode = data.mode?.name || data.mode || null;
        this.hideButtons(); // safety net: a stale in-input group can't linger into the new mode
        // The Create menu is per-Advice-session; a new mode re-derives it from
        // scratch (or leaves it absent).
        this.setCreateMenu(null);
        this.updatePlaceholder();
      });
      this.eventBus.on('mode:restored', (data) => {
        this.currentMode = data.mode?.name || data.mode || null;
        // A restored session is an existing conversation — never a
        // first-message context, so autocomplete stays closed (this also
        // covers a fresh page load straight into a restored session, where
        // the constructor's `false` would otherwise leave the gate open).
        this.hasSubmittedFirstMessage = true;
        this.updatePlaceholder();
      });
      // mode:cleared fires on dashboard return — reset to no-mode placeholder
      // so the input doesn't keep showing copy tied to the just-exited mode.
      this.eventBus.on('mode:cleared', () => {
        this.currentMode = null;
        this.hasSubmittedFirstMessage = false;
        this.customPlaceholder = false;
        this.hideButtons(); // safety net: never carry in-input buttons across navigation
        this.setCreateMenu(null);
        this.updatePlaceholder();
      });

      // Input disable/enable (questionnaire, daily-message-limit, etc.)
      this.eventBus.on('input:disabled', ({ title, subtitle, variant, link } = {}) => {
        this.showDisabledOverlay(title, subtitle, variant, link);
      });
      this.eventBus.on('input:enabled', () => {
        this.hideDisabledOverlay();
      });

      // Attention animation control for services to draw user attention to input
      this.eventBus.on('input:attention', ({ active }) => {
        if (active) {
          this.triggerAttentionAnimation();
        }
      });

      // Focus control for services to programmatically focus input
      this.eventBus.on('input:focus', () => {
        this.focus();
      });
    }

    this._restoreDraft();

    // Initialize tooltips for action buttons
    this.initializeTooltips();

    // Set initial placeholder
    this.updatePlaceholder();
  }

  /**
   * Handle input field changes - enable/disable submit button
   */
  handleChange() {
    if (!this.chatInput) return;

    // Update button state (respects isSubmitting flag and audio state)
    this.updateButtonState();
  }

  /**
   * Content autosize + STICKY two-row toggle.
   *
   * Two behaviors, deliberately separate:
   *  - Textarea HEIGHT: two-way — fits the content, growing AND shrinking line by
   *    line as text is added/removed (up to the scroll-area's 7-line cap).
   *  - LAYOUT (single-row grid ↔ two-row `.chat-input--expanded`): STICKY — expands
   *    once the content wraps past one line, then STAYS expanded while any text
   *    remains. It collapses back to the single row ONLY when the input is emptied
   *    (backspace-all / select-all-delete). It does NOT drop back the moment the
   *    remaining text happens to fit one line — that would make the box flip-flop
   *    while editing around the wrap boundary.
   *
   * Ordering matters: the textarea is narrower in the single-row grid (it shares the
   * row with the +/mic/submit buttons) than when expanded (full width). We decide the
   * (one-way) expand at the current narrow width, then measure the height AFTER the
   * class is applied so it reflects the final width — all synchronous, no flicker.
   */
  autoExpand() {
    if (!this.chatInput) return;

    // Placeholder wrapping contributes to a textarea's scrollHeight in browsers.
    // An empty composer must therefore use the captured rows="1" baseline rather
    // than treating the placeholder as authored multi-line content.
    if (this.getValue().length === 0) {
      this.container?.classList.remove('chat-input--expanded');
      this.chatInput.style.height = this._singleRowHeight
        ? `${this._singleRowHeight}px`
        : 'auto';
      return;
    }

    if (this.container && this._singleRowHeight) {
      if (!this.container.classList.contains('chat-input--expanded')) {
        // Not yet expanded: expand once the content wraps past one line at the
        // current single-row (narrow) width. Sticky thereafter (until emptied).
        this.chatInput.style.height = 'auto';
        if (this.chatInput.scrollHeight > this._singleRowHeight + SINGLE_ROW_EXPAND_SLACK_PX) {
          this.container.classList.add('chat-input--expanded');
        }
      }
    }

    // Fit the textarea to its content height at the final width (two-way autosize).
    this.chatInput.style.height = 'auto';
    this.chatInput.style.height = `${this.chatInput.scrollHeight}px`;
  }

  /**
   * Collapse the textarea back to a single row (instant, no height transition).
   * Used by clear()/after-submit and by the dashboard↔chat FLIP (called by
   * PageManager BEFORE it measures the input position, so a sticky-expanded box
   * can't shift `top` and cause a jump).
   */
  resetInputHeight() {
    // Collapse the sticky two-row expansion back to the single-row base. This is
    // the sole revert point for `.chat-input--expanded` — covers clear()/after
    // submit, the dashboard↔chat FLIP reset (PageManager calls this before it
    // measures the input position), and recording start (_startWaveform).
    this.container?.classList.remove('chat-input--expanded');
    if (!this.chatInput) return;
    if (this._singleRowHeight) {
      this.chatInput.style.height = `${this._singleRowHeight}px`;
    } else {
      this.chatInput.style.height = 'auto';
    }
  }

  /**
   * Show disabled overlay with title, optional subtitle, and optional variant class.
   * The overlay is a normal-height title + subtitle box everywhere. On chat pages the
   * call-to-action (e.g. the usage-lock "Upgrade" button) lives in the floating PillRow
   * above the input; on dashboard (where the pill row never renders) a caller can pass
   * `link` to append an inline text-link CTA to the subtitle instead.
   * @param {string} title - Primary message displayed prominently
   * @param {string} [subtitle] - Secondary message displayed below
   * @param {string} [variant] - Optional modifier applied to the chat-input
   *   container as `.chat-input--{variant}` so callers can customize styling
   *   (e.g. an amber fill for `usage-locked`).
   * @param {{label: string, page?: string}} [link] - Optional inline CTA appended
   *   after the subtitle; clicking emits `navigate` to `link.page` (default 'upgrade').
   */
  showDisabledOverlay(title = 'Please choose an option from above', subtitle = '', variant = null, link = null) {
    this._closePlusDropdown();

    // A usage-lock / questionnaire deep-link can fire mid-recording and cover the
    // mic/stop button — cancel any active recording so it can't be left orphaned.
    if (this.audioService && this.audioState !== 'idle') {
      this.audioService.cancelRecording();
      this.showNotification('Recording cancelled', 'info');
    }

    if (!this.inputDisabledOverlay) return;

    // Simple layout: condensed message, no subtitle
    const isSimple = this.container?.classList.contains('chat-input--simple');
    const displayTitle = isSimple ? 'Chat is disabled during questionnaire' : title;
    const displaySubtitle = isSimple ? '' : subtitle;

    const titleEl = this.inputDisabledOverlay.querySelector('.chat-input__disabled-title');
    const subtitleEl = this.inputDisabledOverlay.querySelector('.chat-input__disabled-subtitle');

    if (titleEl) titleEl.textContent = displayTitle;
    if (subtitleEl) {
      subtitleEl.textContent = displaySubtitle;
      const displayLink = isSimple ? null : link;
      if (displayLink?.label) {
        if (displaySubtitle) subtitleEl.append(' ');
        const linkBtn = document.createElement('button');
        linkBtn.type = 'button';
        linkBtn.className = 'chat-input__disabled-link';
        linkBtn.textContent = displayLink.label;
        linkBtn.addEventListener('click', () => {
          const targetPage = displayLink.page || 'upgrade';
          if (targetPage === 'upgrade') {
            // Usage-lock dashboard overlay CTA — goes through the shared
            // upgrade-navigation contract so the click is counted.
            goToUpgrade(this.eventBus, 'lockOverlayLink');
          } else {
            this.eventBus?.emit?.('navigate', { page: targetPage });
          }
        });
        subtitleEl.appendChild(linkBtn);
      }
    }

    // Swap variant class on the chat-input container for visual customization.
    if (this.container) {
      if (this._disabledVariantClass) {
        this.container.classList.remove(this._disabledVariantClass);
      }
      this._disabledVariantClass = variant ? `chat-input--${variant}` : null;
      if (this._disabledVariantClass) {
        this.container.classList.add(this._disabledVariantClass);
      }
    }

    this.inputDisabledOverlay.removeAttribute('hidden');
  }

  /**
   * Hide disabled overlay
   */
  hideDisabledOverlay() {
    if (!this.inputDisabledOverlay) return;

    if (this.container && this._disabledVariantClass) {
      this.container.classList.remove(this._disabledVariantClass);
      this._disabledVariantClass = null;
    }

    this.inputDisabledOverlay.setAttribute('hidden', '');
  }

  /**
   * Mount an action-button group inside the chat input, replacing the textarea.
   * Modal by default; pass `allowTextEntry` to surface the "respond in your own
   * words" fallback link. Idempotent by `groupId` — re-calling with any groupId
   * swaps the mounted group rather than stacking.
   *
   * Rendering is owner-driven: callers decide when to show/hide. An active disabled
   * overlay (questionnaire/usage-lock) sits above this region via z-index, so the
   * buttons simply render beneath it until the overlay clears.
   *
   * @param {HTMLElement} groupElement - A `.action-buttons` group (from ActionButtons.createButtonGroup)
   * @param {Object} [options]
   * @param {boolean} [options.allowTextEntry=false] - Show the text fallback link
   * @param {string} [options.textEntryLabel] - Per-group copy for that link; falls back
   *   to the markup default ("Or, click to respond in your own words")
   * @param {string} [options.groupId] - Identifier for idempotency / ownership guard
   */
  showButtons(groupElement, { allowTextEntry = false, textEntryLabel = null, groupId = null } = {}) {
    if (!this.buttonsEl || !this.buttonsSlot || !groupElement) return;

    // Mounting an action group hides the whole flex row (incl. mic/stop). Cancel any
    // active recording first so the user can't be stranded mid-record behind buttons.
    if (this.audioService && this.audioState !== 'idle') {
      this.audioService.cancelRecording();
    }

    // Entering the buttons state — drop any stash + "Back to buttons" affordance.
    this._clearStashedButtons();

    // Idempotent: replace whatever is currently mounted.
    this.buttonsSlot.replaceChildren(groupElement);
    this._activeButtonGroupId = groupId;
    if (this.textFallbackBtn) {
      this.textFallbackBtn.textContent = textEntryLabel || this._defaultTextFallbackLabel;
      this.textFallbackBtn.hidden = !allowTextEntry;
    }
    // Hide the textarea/action bar, reveal the buttons region (instant swap — the
    // input snaps between its small and large heights rather than animating).
    this._setFlexHidden(true);
    this.buttonsEl.removeAttribute('hidden');
  }

  /**
   * Remove the mounted button group and restore the textarea. No-op if a different
   * `groupId` currently owns the region (ownership guard prevents one flow tearing
   * down another flow's buttons). This is the terminal/owner teardown — it also
   * drops any stashed group + the "Back to buttons" affordance.
   * @param {Object} [options]
   * @param {string} [options.groupId] - When provided, only hide if it owns the region
   */
  hideButtons({ groupId = null } = {}) {
    if (!this.buttonsEl) return;
    if (groupId && this._activeButtonGroupId && groupId !== this._activeButtonGroupId) return;

    this._clearStashedButtons();

    // Instant swap back to the textarea — the input snaps between heights.
    this._teardownButtonsEndState();
  }

  /**
   * @private
   * Synchronously mutate the DOM to the "textarea visible, buttons gone" end-state.
   * Shared by the owner teardown (`hideButtons`) and the text-fallback path.
   */
  _teardownButtonsEndState() {
    this.buttonsEl.setAttribute('hidden', '');
    if (this.buttonsSlot) this.buttonsSlot.replaceChildren();
    if (this.textFallbackBtn) this.textFallbackBtn.hidden = true;
    this._activeButtonGroupId = null;
    this._setFlexHidden(false);
  }

  /**
   * @returns {boolean} Whether a button group is currently mounted in the input.
   */
  hasButtons() {
    return !!this.buttonsEl && !this.buttonsEl.hasAttribute('hidden');
  }

  /**
   * The groupId that currently *owns* the input region — whether the group is
   * mounted (`_activeButtonGroupId`) OR temporarily stashed behind the textarea
   * because the user chose "respond in your own words" (`_stashedButtons.groupId`).
   * Stash-aware on purpose: the central teardown re-read and Discovery's
   * `_rerenderActiveButtons` both run *after* a text-fallback, when
   * `_activeButtonGroupId` is already null but the group is still logically owned.
   * @returns {string|null}
   */
  getOwningButtonGroupId() {
    return this._activeButtonGroupId ?? this._stashedButtons?.groupId ?? null;
  }

  /**
   * @private
   * Show/hide the textarea + action-bar flex wrapper. Backed by a CSS rule
   * (`.chat-input__flex[hidden]`) so the explicit flex display doesn't defeat it.
   */
  _setFlexHidden(hidden) {
    const flex = this.container?.querySelector('.chat-input__flex');
    if (!flex) return;
    if (hidden) {
      flex.setAttribute('hidden', '');
    } else {
      flex.removeAttribute('hidden');
    }
  }

  /**
   * @private
   * User clicked "respond in your own words". Stash the live group so it can be
   * restored via the "Back to buttons" pill, announce the stash change, then swap to
   * the textarea and focus it. Per design decision O3 the active-button registry is
   * intentionally LEFT intact, so Discovery's hybrid classifier still routes the
   * typed reply against the on-screen choices; the owner clears the registry only
   * on terminal transitions (complete/skip/supersede).
   */
  _handleTextFallbackClick() {
    // Capture the mounted group (the reference survives the slot being emptied) so
    // the user can toggle back to it.
    const group = this.buttonsSlot?.firstElementChild || null;
    if (group) {
      this._stashedButtons = {
        group,
        groupId: this._activeButtonGroupId,
        allowTextEntry: !!(this.textFallbackBtn && !this.textFallbackBtn.hidden),
        textEntryLabel: this.textFallbackBtn?.textContent?.trim() || null
      };
      // The Back-to-Buttons affordance now lives in the PillRow, driven by this event.
      this.eventBus?.emit?.('input:stash-changed');
    }

    // Instant swap to the textarea — the input snaps between heights.
    this._teardownButtonsEndState();
    this.focus();
  }

  /**
   * Whether a button group is currently stashed behind the textarea (the user chose
   * "respond in your own words"). Drives the PillRow's Back-to-Buttons pill.
   * @returns {boolean}
   */
  hasStashedButtons() {
    return !!this._stashedButtons;
  }

  /**
   * Re-mount the group stashed by the text fallback (the "Back to buttons" action).
   * `showButtons` clears the stash and emits `input:stash-changed`, so the pill hides.
   */
  restoreStashedButtons() {
    const stash = this._stashedButtons;
    if (!stash?.group) {
      this._clearStashedButtons();
      return;
    }
    this.showButtons(stash.group, {
      allowTextEntry: stash.allowTextEntry,
      textEntryLabel: stash.textEntryLabel,
      groupId: stash.groupId
    });
  }

  /**
   * @private
   * Drop any stashed group and announce the stash change so the PillRow's
   * Back-to-Buttons pill re-evaluates. Centralized here (plus `_handleTextFallbackClick`)
   * so all cross-file `hideButtons` callers — including the async
   * `ActionRouter._teardownOwnedGroup` — funnel through a single emit.
   */
  _clearStashedButtons() {
    const hadStash = !!this._stashedButtons;
    this._stashedButtons = null;
    this._stashPillSuppressed = false;
    if (hadStash) this.eventBus?.emit?.('input:stash-changed');
  }

  /**
   * Hide the "Back to buttons" pill WITHOUT dropping the stash itself. Called by
   * `ModeOrchestrator._applyStashDropGuard` when a typed detour is submitted over
   * a stashed DURABLE-proposal group (`partner-memo-review` /
   * `practice-proposal-decision`) — the stash must survive as a state carrier for
   * Phase 3.6's auto-restore, but the pill would otherwise dangle a "go back"
   * affordance mid-detour, when the input should look completely normal
   * (app-tools plan Phase 3.5, decision #8). No-op if nothing is stashed or the
   * pill is already suppressed.
   */
  suppressStashedButtonsPill() {
    if (!this._stashedButtons || this._stashPillSuppressed) return;
    this._stashPillSuppressed = true;
    this.eventBus?.emit?.('input:stash-changed');
  }

  /**
   * Whether the "Back to buttons" pill should stay hidden even though a group is
   * currently stashed (mid-detour over a durable-proposal group). Consulted
   * alongside `hasStashedButtons()` by the pill's `visibleWhen` gate.
   * @returns {boolean}
   */
  isStashedButtonsPillSuppressed() {
    return this._stashPillSuppressed;
  }

  /**
   * Get input value
   * @returns {string}
   */
  getValue() {
    return this.chatInput?.value || '';
  }

  /**
   * Set input value
   * @param {string} value - Value to set
   */
  setValue(value) {
    if (this.chatInput) {
      this.chatInput.value = value;
      this.handleChange();
      this.autoExpand();
      this._saveDraft();
    }
  }

  /**
   * Get the tab-scoped storage key for the current composer context.
   * @returns {string}
   * @private
   */
  _getDraftKey() {
    return `chatDraft:${this.currentDraftSessionId || 'new'}`;
  }

  /**
   * Debounce draft persistence. The eventual writer reads the live textarea.
   * @private
   */
  _scheduleDraftSave() {
    if (this.draftDebounceTimer) clearTimeout(this.draftDebounceTimer);
    this.draftDebounceTimer = setTimeout(() => {
      this.draftDebounceTimer = null;
      this._saveDraft();
    }, DRAFT_DEBOUNCE_MS);
  }

  /**
   * Persist the current text draft, or remove its key when the composer is empty.
   * Storage failures are non-fatal because some browsers disable web storage.
   * @private
   */
  _saveDraft() {
    try {
      const key = this._getDraftKey();
      const value = this.getValue();
      if (value) sessionStorage.setItem(key, value);
      else sessionStorage.removeItem(key);
    } catch (_) {
      // Draft persistence is best-effort.
    }
  }

  /**
   * Restore the current context's draft without displacing typed text or buttons.
   * @private
   */
  _restoreDraft() {
    if (!this.chatInput || this.getValue() !== '' || this.hasButtons()) return;
    try {
      const draft = sessionStorage.getItem(this._getDraftKey());
      if (!draft) return;
      this.chatInput.value = draft;
      this.handleChange();
      this.autoExpand();
      this.updateButtonState();
    } catch (_) {
      // Draft restoration is best-effort.
    }
  }

  /**
   * Cancel delayed persistence and remove drafts consumed by a send action.
   * Clears both keys so dashboard text cannot reappear after lazy session creation.
   * @private
   */
  _clearConsumedDrafts() {
    if (this.draftDebounceTimer) {
      clearTimeout(this.draftDebounceTimer);
      this.draftDebounceTimer = null;
    }
    try {
      sessionStorage.removeItem('chatDraft:new');
      sessionStorage.removeItem(this._getDraftKey());
    } catch (_) {
      // Draft persistence is best-effort.
    }
  }

  /**
   * Populate input with text and trigger autocomplete.
   * Used by shortcut buttons to show curated suggestions.
   * @param {string} value - Value to populate
   */
  populateAndShowAutocomplete(value) {
    // Cancel any pending blur-triggered hide (e.g., when switching between shortcut buttons)
    this.cancelPendingAutocompleteHide();
    this.setValue(value);
    this.focus();
    // Trigger autocomplete immediately (bypass debounce)
    this.updateAutocomplete();
  }

  /**
   * Cancel a delayed blur-triggered autocomplete hide.
   */
  cancelPendingAutocompleteHide() {
    if (this.blurHideTimeout) {
      clearTimeout(this.blurHideTimeout);
      this.blurHideTimeout = null;
    }
  }

  /**
   * Clear input and collapse the sticky-expanded box back to one row.
   */
  clear() {
    this.setValue('');
    this.resetInputHeight();
  }

  /**
   * Set pending mode for dashboard (deferred mode switch)
   * @param {string} mode - The mode to pre-select
   */
  setPendingMode(mode) {
    this.pendingMode = mode;
  }

  /**
   * Clear pending mode
   */
  clearPendingMode() {
    this.pendingMode = null;
  }

  /**
   * Get pending mode
   * @returns {string|null} The pending mode or null
   */
  getPendingMode() {
    return this.pendingMode;
  }

  /**
   * Focus input
   */
  focus() {
    if (this.chatInput) {
      this.chatInput.focus();
    }
  }

  /**
   * Focus the input to anchor a dashboard picker dropdown.
   *
   * On pointer/desktop devices we focus the textarea so the existing blur
   * handler dismisses the dropdown on click-away. On touch devices focusing
   * the textarea would pop up the soft keyboard, so instead we apply the
   * focused styling and install a click-away dismissal that mirrors the
   * blur behaviour (since the autocomplete has no other outside-click hide).
   */
  focusForPicker() {
    const isTouch = window.matchMedia?.('(pointer: coarse)')?.matches;
    if (!isTouch) {
      this.focus();
      return;
    }
    this.setFocused(true);
    if (this._pickerDismissActive) return;
    this._pickerDismissActive = true;
    // Defer so the click that opened the picker doesn't immediately close it.
    setTimeout(() => {
      document.addEventListener('click', this._handlePickerOutsideClick);
    }, 0);
  }

  /**
   * Remove the touch click-away dismissal installed by focusForPicker().
   */
  _teardownPickerDismiss() {
    if (!this._pickerDismissActive) return;
    this._pickerDismissActive = false;
    document.removeEventListener('click', this._handlePickerOutsideClick);
    this.setFocused(false);
  }

  /**
   * Hide the dashboard picker when a touch user taps outside the dropdown.
   * @param {Event} event - Click event
   */
  _handlePickerOutsideClick(event) {
    if (this.autocompleteContainer?.contains(event.target)) return;
    this.hideAutocomplete();
  }

  /**
   * Handle message submission
   */
  handleSubmit() {
    if (this._isLocked()) return;
    this._closePlusDropdown();

    // An attachment still being read or decoded would be dropped by this
    // submit's clear. Hold the send rather than silently losing it.
    if (this._pendingIngestions > 0) {
      window.notificationManager?.showInfo('Still adding your image — try again in a moment');
      return;
    }

    const message = this.getValue().trim();
    const hasImages = this.attachedImages.length > 0;

    // Require either text or images
    if (!message && !hasImages) return;

    const pendingMode = this.pendingMode;

    // Prepare images payload (base64 for API + display)
    const images = this.attachedImages.map(img => ({
      mimeType: img.mimeType,
      data: img.base64,
      filename: img.filename
    }));

    this.hasSubmittedFirstMessage = true;
    this.isSubmitting = true;
    this._submitStartedAt = Date.now();

    this.triggerSubmitPulse();
    this._clearConsumedDrafts();
    this.clear();
    this.clearAttachments();
    this.clearPendingMode();
    this.updateButtonState();

    if (this.eventBus) {
      this.eventBus.emit('message:send', {
        message,
        images: images.length > 0 ? images : undefined,
        mode: pendingMode || undefined
      });
    }
  }

  /**
   * Handle plus button click — toggles attachment dropdown
   */
  handlePlusClick() {
    if (this.attachedImages.length >= this.maxImages) {
      window.notificationManager?.showError(`Maximum ${this.maxImages} images allowed`);
      return;
    }

    if (this.plusDropdownOpen) {
      this._closePlusDropdown();
    } else {
      this._openPlusDropdown();
    }
  }

  /**
   * Open plus dropdown menu
   */
  _openPlusDropdown() {
    if (!this.plusDropdown) return;
    // The two in-input menus sit on opposite edges of the same bar — only one
    // popover open at a time keeps them from overlapping/competing for outside-click.
    this._closeCreateMenu();
    this.plusDropdown.hidden = false;
    this.plusDropdownOpen = true;
    this.plusButton?.setAttribute('aria-expanded', 'true');

    // Close autocomplete if open to avoid overlapping popups
    this.hideAutocomplete?.();

    setTimeout(() => {
      document.addEventListener('click', this._handlePlusOutsideClick);
    }, 0);
  }

  /**
   * Close plus dropdown menu
   */
  _closePlusDropdown() {
    if (!this.plusDropdown) return;
    this.plusDropdown.hidden = true;
    this.plusDropdownOpen = false;
    this.plusButton?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._handlePlusOutsideClick);
  }

  /**
   * Handle clicks outside the plus dropdown to close it
   * @param {Event} event - Click event
   */
  _handlePlusOutsideClick(event) {
    if (!this.plusDropdownOpen) return;
    const clickedButton = this.plusButton?.contains(event.target);
    const clickedDropdown = this.plusDropdown?.contains(event.target);
    if (!clickedButton && !clickedDropdown) {
      this._closePlusDropdown();
    }
  }

  /**
   * Route plus dropdown action to appropriate handler
   * @param {string} action - Action identifier ('files' or 'screenshot')
   */
  _handlePlusDropdownAction(action) {
    this._closePlusDropdown();
    if (this.attachedImages.length >= this.maxImages) {
      window.notificationManager?.showError(`Maximum ${this.maxImages} images allowed`);
      return;
    }
    if (action === 'files') {
      this.fileInput.click();
    } else if (action === 'screenshot') {
      this.hasScreenCapture ? this._captureScreen() : this.cameraInput.click();
    }
  }

  // ============================================================
  // CREATE MENU (Advice early-artifact requests)
  // ============================================================

  /**
   * @private
   * One-time wiring for the Create control: the compact trigger's glyph, its
   * icon-only tooltip, and the open/close + selection handlers. The panel itself
   * is populated per descriptor by {@link Input#_renderCreateMenu}.
   */
  _initializeCreateMenu() {
    if (!this.createButton || !this.createMenu) return;

    Tooltip.setup(this.createButton, {
      position: 'below',
      getText: () => 'Actions'
    });

    this.createButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this._toggleCreateMenu();
    });

    this.createMenu.addEventListener('click', (e) => {
      // Header info button first: the Learn affordance is not an action row and
      // must not fall through to the item-dispatch branch.
      if (e.target.closest('[data-learn]')) {
        this._openActionsHelpPanel();
        return;
      }
      const item = e.target.closest('.options-dropdown__item');
      if (!item) return;
      this._handleCreateMenuSelect(item.dataset.action);
    });
  }

  /**
   * Apply a backend `adviceCreateMenu` projection. `null` (or an item list that
   * resolves to nothing) retracts the control entirely — the SPA never keeps a
   * latched trigger, because a latched trigger outlives its membership and would
   * eventually open an empty menu.
   *
   * @param {{items: Array<string>}|null} descriptor - Backend projection, or null
   * @returns {void}
   */
  setCreateMenu(descriptor) {
    const items = Array.isArray(descriptor?.items) ? descriptor.items : null;
    this.createMenuItems = items && items.length > 0 ? items : null;
    // A fresh descriptor invalidates whatever the open panel was offering.
    this._closeCreateMenu();
    this.updateCreateMenuState();
  }

  /**
   * Recompute the Create trigger's visibility. Runs inside the shared
   * {@link Input#updateButtonState} pass, which every input/audio/lifecycle
   * change already funnels through.
   *
   * Visible only when the backend offers items AND the composer is in the state
   * the menu is an alternative TO:
   *  - hidden once there is content (text or images) — the typed path opens the
   *    same intent, and this is what keeps Create and Send from co-rendering;
   *  - hidden while recording/transcribing;
   *  - deliberately VISIBLE during generation (clicks queue through PillDispatch,
   *    exactly like a pill).
   * A mounted in-input button group needs no rule here: `showButtons` hides the
   * whole `.chat-input__flex` row, taking the right cluster with it.
   *
   * @returns {void}
   */
  updateCreateMenuState() {
    if (!this.createContainer) return;

    const entries = resolveAdviceCreateMenuItems(this.createMenuItems);
    const hasContent = this.getValue().trim().length > 0 || this.attachedImages.length > 0;
    const visible = entries.length > 0 && !hasContent && this.audioState === 'idle';

    if (visible) this._renderCreateMenu(entries);
    this.createContainer.hidden = !visible;
    if (!visible) this._closeCreateMenu();
  }

  /**
   * @private
   * Render the panel's rows from resolved catalog buttons. Rows are the shared
   * `.options-dropdown__item` markup ({@link renderMenuItems}) so the Create menu
   * cannot drift from every other menu in the app; `data-action` carries the
   * descriptor's item KEY, which is what the click handler dispatches on.
   * Re-renders only when membership actually changed, so an open panel is not
   * rebuilt underneath the cursor by an unrelated input event.
   *
   * @param {Array<{key:string,label:string,hint:?string}>} entries - Resolved
   *   menu items; `hint` (e.g. the locked "Deep exploration" row's "Opens after
   *   your key takeaways") rides straight through to `renderMenuItems`.
   */
  _renderCreateMenu(entries) {
    const signature = entries.map((entry) => entry.key).join('|');
    if (signature === this._renderedCreateItems) return;
    this.createMenu.innerHTML = (
      // Header, outside the item markup so the shared renderer stays chrome-free.
      // The label names the menu (the trigger is icon-only, so the tooltip is
      // gone the moment the panel opens) and makes the explore-only stub state
      // read as intentional. The quiet info button is the page-header
      // info-button convention at menu scale; it carries the Learn affordance
      // that used to be a footer row. Rebuilt with every membership re-render.
      '<div class="chat-input__create-menu-header">'
      + '<span class="chat-input__create-menu-label" aria-hidden="true">Actions</span>'
      + '<button type="button" role="menuitem" class="chat-input__create-menu-info" data-learn aria-label="Learn about actions">'
      + '<svg width="17" height="17" aria-hidden="true"><use href="#icon-info"/></svg>'
      + '</button>'
      + '</div>'
    ) + renderMenuItems(
      // `icon` is renderMenuItems' per-item override — `practice`/`memo` are
      // descriptor keys, not verbs in the shared MENU_ICON map.
      entries.map((entry) => ({
        action: entry.key,
        label: entry.label,
        icon: entry.icon,
        disabled: entry.disabled,
        hint: entry.hint
      }))
    );
    this._renderedCreateItems = signature;
  }

  /**
   * @private
   * Open the right-panel "Actions" help page describing exactly the actions the
   * menu currently offers. Follows the help-hint convention: no chrome title —
   * the panel body's own heading carries it (see chat/helpHintPanel.js).
   */
  _openActionsHelpPanel() {
    // Deliberately does NOT close the menu: the panel explains the rows the
    // user is looking at, so they stay side by side — read, then pick. The
    // outside-click dismissal exempts the right panel for the same reason.
    const entries = resolveAdviceCreateMenuItems(this.createMenuItems);
    if (entries.length === 0) return;
    const built = buildActionsHelpPanel(entries);
    if (!built) return;
    window.analyticsService?.trackEvent?.('advice_create_menu_learn_opened', {
      component: 'chat_input',
      items: entries.map((entry) => entry.key).join('|')
    });
    this.eventBus?.emit('right-panel:open', {
      title: built.title,
      content: built.content,
      contentType: 'help-hint'
    });
  }

  /** @private Toggle the Create panel. */
  _toggleCreateMenu() {
    if (this.createMenuOpen) {
      this._closeCreateMenu();
    } else {
      this._openCreateMenu();
    }
  }

  /**
   * @private
   * Open the Create panel (same contract as the plus dropdown: outside-click
   * dismissal armed on the next tick so the opening click can't close it).
   */
  _openCreateMenu() {
    if (!this.createMenu || this.createContainer?.hidden) return;
    // Mirrors the plus dropdown's own exclusivity — see _openPlusDropdown.
    this._closePlusDropdown();
    this.createMenu.hidden = false;
    this.createMenuOpen = true;
    this.createButton?.setAttribute('aria-expanded', 'true');
    this.hideAutocomplete?.();

    window.analyticsService?.trackEvent?.('advice_create_menu_opened', {
      component: 'chat_input',
      trigger: 'icon'
    });

    setTimeout(() => {
      document.addEventListener('click', this._handleCreateOutsideClick);
    }, 0);
  }

  /** @private Close the Create panel. Idempotent. */
  _closeCreateMenu() {
    if (!this.createMenu) return;
    this.createMenu.hidden = true;
    this.createMenuOpen = false;
    this.createButton?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._handleCreateOutsideClick);
  }

  /**
   * @private
   * Close the Create panel on a click outside it.
   * @param {Event} event - Click event
   */
  _handleCreateOutsideClick(event) {
    if (!this.createMenuOpen) return;
    const clickedButton = this.createButton?.contains(event.target);
    const clickedMenu = this.createMenu?.contains(event.target);
    // The right panel doesn't dismiss: the header ⓘ opens the Actions help page
    // beside the open menu, and interacting with that page (scrolling, even
    // closing it) must not yank the menu the page is explaining.
    const clickedRightPanel = Boolean(event.target.closest?.('.right-panel'));
    if (!clickedButton && !clickedMenu && !clickedRightPanel) {
      this._closeCreateMenu();
    }
  }

  /**
   * @private
   * Hand a chosen item to the dispatcher. The panel closes FIRST and stays
   * closed until a fresh descriptor arrives, so a second click cannot land
   * against membership the first click just invalidated.
   *
   * @param {string} item - Descriptor item key ('practice' | 'memo')
   */
  _handleCreateMenuSelect(item) {
    if (!item) return;
    this._closeCreateMenu();
    window.analyticsService?.trackEvent?.('advice_create_menu_selected', {
      component: 'chat_input',
      trigger: item
    });
    this.eventBus?.emit('advice:create-menu-select', { item });
  }

  /**
   * Capture screen via getDisplayMedia (desktop)
   */
  async _captureScreen() {
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor' }
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => (result ? resolve(result) : reject(new Error('Screen capture produced no image'))),
          'image/png'
        );
      });

      const filename = `screenshot-${Date.now()}.${extensionForType(blob.type)}`;
      const file = new File([blob], filename, { type: blob.type });

      // Routed through the shared queue rather than pushed straight into
      // attachedImages: this path previously bypassed serialization, the
      // attachment limit, and type/size validation — it was the one way to
      // end up with a fifth attachment.
      await this._enqueueImageFiles([file], { fallbackPrefix: 'screenshot' });
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
        console.error('[Input] Screen capture failed:', err);
        window.notificationManager?.showError('Screen capture failed');
      }
    } finally {
      stream?.getTracks().forEach(t => t.stop());
    }
  }

  /**
   * Create hidden camera input for mobile photo capture
   * @returns {HTMLInputElement}
   */
  _createCameraInput() {
    const input = document.createElement('input');
    input.type = 'file';
    // Derived, not `image/*`: a wider accept than validation allows meant the
    // camera could hand back a type we then rejected after the fact.
    input.accept = pickerAcceptAttr();
    input.capture = 'environment';
    input.style.display = 'none';
    input.addEventListener('change', (e) => this._handleFileSelect(e));
    document.body.appendChild(input);
    return input;
  }

  /**
   * Set focused state on container
   * @param {boolean} focused - Whether the input is focused
   */
  setFocused(focused) {
    if (!this.container) return;
    this.container.classList.toggle('chat-input--focused', focused);
  }

  /**
   * Trigger the submission pulse animation
   */
  triggerSubmitPulse() {
    if (!this.container) return;

    // Remove class first to allow re-triggering
    this.container.classList.remove('chat-input--submitting');

    // Force reflow to restart animation
    void this.container.offsetWidth;

    // Add class to trigger animation
    this.container.classList.add('chat-input--submitting');

    // Remove after animation completes (300ms)
    setTimeout(() => {
      this.container.classList.remove('chat-input--submitting');
    }, 300);
  }

  /**
   * Trigger the attention animation on the input.
   * Used to draw user attention to the input field with a brief vertical bounce.
   * Animation runs once and completes automatically.
   */
  triggerAttentionAnimation() {
    if (!this.container) return;

    // The animation lives on .chat-input__inner (the visible box). We only need a
    // node to force a reflow on so the animation can re-trigger.
    const box = this.container.querySelector('.chat-input__inner');
    if (!box) return;

    // Remove class first to allow re-triggering if already present
    this.container.classList.remove('chat-input--attention');

    // Force reflow to restart animation
    void box.offsetWidth;

    // Add class to trigger animation
    this.container.classList.add('chat-input--attention');
  }

  /**
   * Stop the attention pulse animation.
   * Called when user starts typing to immediately stop the attention-drawing animation.
   */
  stopAttentionPulse() {
    if (!this.container) return;
    this.container.classList.remove('chat-input--attention');
  }

  /**
   * Handle stopping message generation
   * Delegates stop ownership to ChatManager.
   * @returns {Promise<void>|undefined}
   */
  handleStop() {
    return this.chatManager?.stopGeneration?.();
  }

  /**
   * Set ChatManager reference (called after ChatManager is initialized)
   * @param {Object} chatManager - ChatManager instance
   */
  setChatManager(chatManager) {
    this.chatManager = chatManager;
  }

  /**
   * Reset submission state
   */
  resetSubmitState() {
    this.isSubmitting = false;
    this._submitStartedAt = null;
    this.updateButtonState();
  }

  /**
   * Refill the generic composer from a transport payload. Existing typed text
   * is never overwritten; transport images are rehydrated without File objects.
   *
   * @param {{text?:string,images?:Array<Object>}} payload
   * @returns {void}
   */
  refillComposer({ text = '', images = [] } = {}) {
    if (this.getValue() === '') {
      this.setValue(text);
      this.updateButtonState();
      this.focus();
      this._announce('Message returned to editor');
    }

    for (const image of images) {
      if (this.attachedImages.length >= this.maxImages) break;
      this._addTransportImage(image);
    }
  }

  /**
   * True when the chat is busy for ANY reason — either a user-dispatched
   * LLM request is in flight (`isSubmitting`) or a programmatic message is
   * streaming/animating (`chatBusy`). All submit gates use this.
   * @private
   */
  _isLocked() {
    return this.isSubmitting
      || this.chatBusy
      || Boolean(window.app?.services?.usage?.isBlocked?.());
  }

  /**
   * Public entry to refresh every state-driven control in the action bar (mic,
   * submit, Create). All callers (text change, audio state change, submit
   * lifecycle, chat busy/idle) fan out through here so they never drift out of
   * sync.
   */
  updateButtonState() {
    this.updateMicButtonState();
    this.updateSubmitButtonState();
    this.updateCreateMenuState();
  }

  // ============================================================
  // AUTOCOMPLETE METHODS
  // ============================================================

  /**
   * Handle input changes for autocomplete (debounced)
   */
  handleAutocompleteInput() {
    if (!this.autocompleteService || !this.autocompleteContainer) return;

    // Clear existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Debounce the autocomplete search
    this.debounceTimer = setTimeout(() => {
      this.updateAutocomplete();
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  }

  /**
   * Update autocomplete suggestions based on current input.
   * Only shows autocomplete before first message is submitted in session.
   * Checks for shortcut triggers first, then falls back to keyword matching.
   */
  updateAutocomplete() {
    if (!this.autocompleteService || !this.autocompleteContainer) return;

    // Don't show autocomplete after first message has been submitted
    if (this.hasSubmittedFirstMessage) {
      this.hideAutocomplete();
      return;
    }

    const input = this.getValue();

    // Check for shortcut trigger first (exact match on trigger phrase)
    const shortcutSuggestions = this.autocompleteService.getShortcutSuggestions(input);
    if (shortcutSuggestions) {
      this.renderAutocomplete(shortcutSuggestions, input);
      this.showAutocomplete();
      return;
    }

    // Fall back to keyword matching
    const suggestions = this.autocompleteService.getSuggestions(input, 6, this.currentMode);

    if (suggestions.length > 0) {
      this.renderAutocomplete(suggestions, input);
      this.showAutocomplete();
    } else {
      this.hideAutocomplete();
    }
  }

  /**
   * Render autocomplete suggestions with highlighted matching words
   * @param {Array<Object>} suggestions - Array of suggestion objects
   * @param {string} input - Current user input for highlighting
   */
  renderAutocomplete(suggestions, input) {
    if (!this.autocompleteList) return;

    // Reset keyboard selection when suggestions change
    this.selectedIndex = -1;

    // Get words to highlight (non-stopwords from input) - only for non-shortcut suggestions
    const wordsToHighlight = getFilteredWords(input);
    const rawInput = input.trim();

    this.autocompleteList.innerHTML = suggestions
      .map(suggestion => {
        // Shortcut suggestions: highlight the trigger prefix, mode defaults to ADVICE
        const displayText = suggestion.isShortcut
          ? this.highlightShortcutPrefix(suggestion.text, suggestion.highlightPrefix)
          : this.highlightMatches(suggestion.text, wordsToHighlight, rawInput);

        return `
          <button
            type="button"
            class="chat-input__autocomplete-item"
            data-suggestion-id="${suggestion.id}"
            data-suggestion-text="${this.escapeHtml(suggestion.text)}"
            data-suggestion-category="${suggestion.category || ''}"
            data-suggestion-mode="${suggestion.mode || 'ADVICE'}"
          >
            ${displayText}
          </button>
        `;
      })
      .join('');
  }

  /**
   * Highlight matching text in suggestion.
   * Priority: 1) Prefix match (entire prefix highlighted), 2) Exact keyword matches
   * @param {string} text - The suggestion text
   * @param {string[]} words - Keywords to highlight (lowercased, stopwords removed)
   * @param {string} rawInput - Raw user input for prefix matching
   * @returns {string} HTML with matching text wrapped in <mark>
   */
  highlightMatches(text, words, rawInput) {
    // 1. Check for prefix match (suggestion starts with what user typed)
    const inputLower = rawInput.toLowerCase();
    const textLower = text.toLowerCase();

    if (inputLower.length >= 2 && textLower.startsWith(inputLower)) {
      // Highlight the matching prefix
      const prefixLength = rawInput.length;
      const prefix = text.substring(0, prefixLength);
      const rest = text.substring(prefixLength);
      return `<mark>${this.escapeHtml(prefix)}</mark>${this.escapeHtml(rest)}`;
    }

    // 2. Fall back to keyword highlighting (exact word matches only)
    if (!words || words.length === 0) {
      return this.escapeHtml(text);
    }

    let result = this.escapeHtml(text);

    for (const word of words) {
      if (word.length < 2) continue;

      // Escape regex special characters
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Match exact words only (word boundaries on both sides)
      const regex = new RegExp(`\\b(${escapedWord})\\b`, 'gi');

      result = result.replace(regex, '<mark>$1</mark>');
    }

    return result;
  }

  /**
   * Highlight shortcut trigger prefix in suggestion text.
   * Used for shortcut button suggestions where we know the exact prefix to bold.
   * @param {string} text - Full suggestion text
   * @param {string} prefix - Trigger prefix to highlight
   * @returns {string} HTML with prefix wrapped in <mark>
   */
  highlightShortcutPrefix(text, prefix) {
    if (!prefix || !text.toLowerCase().startsWith(prefix.toLowerCase())) {
      return this.escapeHtml(text);
    }

    const prefixLength = prefix.length;
    const matchedPrefix = text.substring(0, prefixLength);
    const rest = text.substring(prefixLength);
    return `<mark>${this.escapeHtml(matchedPrefix)}</mark>${this.escapeHtml(rest)}`;
  }

  /**
   * Show the autocomplete dropdown
   */
  showAutocomplete() {
    if (!this.autocompleteContainer) return;

    this.autocompleteContainer.removeAttribute('hidden');
    this.autocompleteVisible = true;
  }

  /**
   * Hide the autocomplete dropdown
   */
  hideAutocomplete() {
    if (!this.autocompleteContainer) return;

    this._teardownPickerDismiss();
    this.autocompleteContainer.setAttribute('hidden', '');
    this.autocompleteContainer.classList.remove('chat-input__autocomplete--dashboard-picker');
    this.autocompleteVisible = false;
    this.selectedIndex = -1;
    this.updateAutocompleteSelection();
    this.eventBus?.emit('autocomplete:hidden');
  }

  /**
   * Navigate autocomplete selection with keyboard
   * @param {number} direction - 1 for down, -1 for up
   */
  navigateAutocomplete(direction) {
    if (!this.autocompleteList) return;

    const items = this.autocompleteList.querySelectorAll('.chat-input__autocomplete-item');
    if (items.length === 0) return;

    // Calculate new index with wrapping
    this.selectedIndex += direction;
    if (this.selectedIndex >= items.length) {
      this.selectedIndex = 0; // Wrap to top
    } else if (this.selectedIndex < 0) {
      this.selectedIndex = items.length - 1; // Wrap to bottom
    }

    this.updateAutocompleteSelection();
  }

  /**
   * Update visual selection state on autocomplete items
   */
  updateAutocompleteSelection() {
    if (!this.autocompleteList) return;

    const items = this.autocompleteList.querySelectorAll('.chat-input__autocomplete-item');
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add('chat-input__autocomplete-item--selected');
        // Scroll item into view if needed
        item.scrollIntoView({ block: 'nearest' });
      } else {
        item.classList.remove('chat-input__autocomplete-item--selected');
      }
    });
  }

  /**
   * Submit the currently selected autocomplete item
   */
  submitSelectedAutocomplete() {
    if (!this.autocompleteList || this.selectedIndex < 0) return;

    const items = this.autocompleteList.querySelectorAll('.chat-input__autocomplete-item');
    const selectedItem = items[this.selectedIndex];

    if (selectedItem) {
      this.handleAutocompleteClick(selectedItem);
    }
  }

  /**
   * Handle click on an autocomplete suggestion
   * @param {HTMLElement} item - The clicked autocomplete item element
   */
  handleAutocompleteClick(item) {
    const text = item.dataset.suggestionText;
    const category = item.dataset.suggestionCategory;
    const mode = item.dataset.suggestionMode;

    if (!text) return;

    // Show selected text in input (visual feedback before page transition)
    this.setValue(text);
    this._clearConsumedDrafts();

    // Hide autocomplete immediately
    this.hideAutocomplete();

    // Mark first message as submitted and set submitting state
    this.hasSubmittedFirstMessage = true;
    this.isSubmitting = true;
    this._submitStartedAt = Date.now();

    // Trigger submission pulse animation
    this.triggerSubmitPulse();

    // Clear input after delay (synced with page transition - clears just before input slide completes)
    setTimeout(() => this.clear(), 400);

    // Update button state
    this.updateButtonState();

    // Emit message with autocomplete metadata and target mode
    // Mode is passed at top level so ModeOrchestrator can switch before processing
    if (this.eventBus) {
      this.eventBus.emit('message:send', {
        message: text,
        mode: mode || undefined,  // Target mode for autocomplete (switches before processing)
        metadata: {
          source: 'autocomplete',
          category: category || undefined
        }
      });
    }
  }

  /**
   * Escape HTML special characters to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    return escapeHtml(text);
  }

  // ============================================================
  // TOOLTIP METHODS
  // ============================================================

  /**
   * Initialize tooltips for action buttons
   */
  initializeTooltips() {
    // Submit button tooltip (dynamic based on state)
    Tooltip.setup(this.submitButton, {
      position: 'below',
      getText: () => this.getSubmitTooltipText()
    });

    // Mic button tooltip (dynamic based on mic-state)
    Tooltip.setup(this.micButton, {
      position: 'below',
      getText: () => this.getMicTooltipText()
    });

    // Plus button tooltip (static)
    Tooltip.setup(this.plusButton, {
      position: 'below',
      getText: () => 'Add photos'
    });
  }

  /**
   * Get tooltip text for submit button based on current state
   * @returns {string|null} Tooltip text, or null if no tooltip should show
   */
  getSubmitTooltipText() {
    if (!this.submitButton || this.submitButton.hidden) return null;
    if (this.isSubmitting) return 'Stop response';
    return this.submitButton.disabled ? null : 'Submit';
  }

  /**
   * Get tooltip text for the mic button based on its current mic-state.
   * @returns {string|null} Tooltip text, or null if no tooltip should show
   */
  getMicTooltipText() {
    if (!this.micButton || this.micButton.hidden) return null;
    const micState = this.micButton.getAttribute('data-mic-state');
    switch (micState) {
      case 'mic':
        return 'Use microphone';
      case 'recording':
        return 'Stop recording';
      case 'processing':
        return null;
      default:
        return null;
    }
  }

  /**
   * Update placeholder text based on location.
   * Skips if a custom placeholder was set by a mode via setPlaceholder().
   */
  updatePlaceholder() {
    if (!this.chatInput) return;

    // Skip if mode set a custom placeholder
    if (this.customPlaceholder) return;

    // Dashboard is the only surface where the user authors an opening message;
    // inside any mode there is always an existing conversation to reply to.
    if (this.currentMode !== null) {
      this.chatInput.placeholder = this.PLACEHOLDER_REPLY;
      return;
    }

    // A null mode is the dashboard/homepage authoring surface. Keep mode and
    // reply placeholders unchanged everywhere else.
    this.chatInput.placeholder = this._placeholderMediaQuery?.matches
      ? this.PLACEHOLDER_INITIAL_MOBILE
      : this.PLACEHOLDER_INITIAL;
  }

  /**
   * Set placeholder text directly.
   * Use this for mode-specific or context-driven placeholder changes.
   * Marks placeholder as "custom" to prevent auto-reset by updatePlaceholder().
   * @param {string} text - The placeholder text to display
   */
  setPlaceholder(text) {
    if (!this.chatInput) return;
    this.chatInput.placeholder = text;
    this.customPlaceholder = true;
  }

  /**
   * Clear custom placeholder flag, allowing updatePlaceholder() to manage placeholder.
   * Called by ModeOrchestrator at start of mode switch to reset state.
   */
  clearCustomPlaceholder() {
    this.customPlaceholder = false;
  }

  // ============================================================
  // FEATURE TOGGLE METHODS
  // ============================================================

  /**
   * Set the FeatureToggleService and listen for layout toggle changes
   * @param {FeatureToggleService} service - The feature toggle service instance
   */
  setFeatureToggleService(service) {
    // Listen for plus button toggle changes
    this.eventBus.on('toggle:plusButton', ({ visible }) => {
      this.plusToggleDisabled = !visible;
      if (this.plusButton) {
        this.plusButton.classList.toggle('chat-input__plus--disabled', !visible);
        this.plusButton.setAttribute('aria-disabled', (!visible).toString());
      }
    });

    // Listen for layout toggle changes
    this.eventBus.on('toggle:chatInputLayout', ({ layout }) => {
      if (this.currentLayout !== layout) {
        this.currentLayout = layout;
        this.applyLayout();
      }
    });

    // Apply initial plus button state
    const plusEnabled = service.get('plusButton');
    this.plusToggleDisabled = !plusEnabled;
    if (this.plusButton) {
      this.plusButton.classList.toggle('chat-input__plus--disabled', !plusEnabled);
      this.plusButton.setAttribute('aria-disabled', (!plusEnabled).toString());
    }
    this.currentLayout = service.get('chatInputLayout');
    this.applyLayout();
  }

  /**
   * Apply current layout class to container
   * 'simple' = single row with text area and submit button
   * 'full' = normal two-row layout with all action buttons
   */
  applyLayout() {
    if (!this.container) return;

    if (this.currentLayout === 'simple') {
      this.container.classList.add('chat-input--simple');
    } else {
      this.container.classList.remove('chat-input--simple');
    }

    console.log(`[Input] Layout applied: ${this.currentLayout}`);
  }

  // ============================================================
  // AUDIO RECORDING METHODS
  // ============================================================

  /**
   * Set the AudioRecordingService instance
   * @param {AudioRecordingService} audioService - The audio service instance
   */
  setAudioService(audioService) {
    this.audioService = audioService;

    // Audio state drives both buttons + the waveform lifecycle.
    this.eventBus.on('audio:state-changed', ({ state }) => {
      this.audioState = state;
      if (state === 'recording') {
        this._startWaveform();
      } else if (state === 'processing') {
        this._freezeWaveform();
      } else {
        this._stopWaveform();
      }
      this.updateButtonState();
    });
    this.eventBus.on('audio:quota-changed', () => {
      this.updateButtonState();
    });

    // Silence / max auto-stop delivers its transcript via an event (Stop behavior:
    // append into the box, no auto-send).
    this.eventBus.on('audio:auto-stop', ({ text }) => {
      if (text && text.trim()) this.injectTranscribedText(text);
    });
    this.eventBus.on('audio:auto-stop-failed', ({ message }) => {
      this.showNotification(message || 'Transcription failed', 'error');
    });

    // Update initial state (show mic if supported and no text)
    this.updateButtonState();
  }

  /**
   * Handle submit button click — send, stop-generation, or submit-during-recording.
   */
  handleSubmitButtonClick() {
    if (this.audioState === 'recording') {
      this.handleSubmitDuringRecording(); // Option B: stop + transcribe + auto-send
    } else if (this.isSubmitting) {
      this.handleStop(); // Stop AI generation
    } else {
      this.handleSubmit(); // Send message
    }
  }

  /**
   * Handle mic button click — start or stop recording based on mic-state.
   */
  handleMicButtonClick() {
    const micState = this.micButton?.getAttribute('data-mic-state');
    if (micState === 'mic') {
      this.handleMicClick();
    } else if (micState === 'recording') {
      this.handleStopRecording();
    }
    // 'processing' — no-op (button disabled).
  }

  /**
   * Handle microphone button click - start recording
   * Includes double-click prevention
   */
  async handleMicClick() {
    if (!this.audioService) return;

    // Prevent double-click race condition
    if (this.audioService.getState() !== 'idle') return;

    // Disable button during state transition
    if (this.micButton) {
      this.micButton.disabled = true;
    }

    try {
      await this.audioService.startRecording();
    } catch (error) {
      this.showNotification(error.message, 'error');
      // Re-enable button on error (state change handler will manage normal flow)
      this.updateButtonState();
    }
  }

  /**
   * Handle stop recording button click — transcribe and append into the box for review.
   */
  async handleStopRecording() {
    if (!this.audioService) return;

    try {
      const text = await this.audioService.stopRecording();
      if (text && text.trim()) {
        this.injectTranscribedText(text);
      }
      // Empty transcript on a manual Stop is a no-op (parity with prior behavior).
    } catch (error) {
      this.showNotification(error.message, 'error');
    }
  }

  /**
   * Submit-during-recording (Option B): stop + transcribe with the in-input
   * "Transcribing…" indicator, then append the transcript and dispatch ONE message
   * through the normal send path (no optimistic bubble, no duplicate). On failure,
   * mirror the Stop path (notify + return to mic; no retry).
   */
  async handleSubmitDuringRecording() {
    if (!this.audioService) return;

    try {
      const text = await this.audioService.stopRecording();
      const transcript = text?.trim();

      if (transcript) {
        this.injectTranscribedText(text);
      }

      // Dispatch the combined value (pre-existing text/images + transcript). If
      // there is genuinely nothing to send, don't post a blank message.
      if (this.getValue().trim() || this.attachedImages.length > 0) {
        this.handleSubmit();
      } else {
        this.showNotification('No speech detected', 'error');
      }
    } catch (error) {
      this.showNotification(error.message, 'error');
    }
  }

  /**
   * Inject transcribed text into the textarea (append-with-space to the end).
   * @param {string} text - Transcribed text to inject
   */
  injectTranscribedText(text) {
    const trimmed = text?.trim();
    if (!trimmed) return;

    const current = this.getValue();
    const newValue = current ? `${current} ${trimmed}` : trimmed;
    this.setValue(newValue);
    this._announce('Transcript inserted');
    this.focus();
  }

  /**
   * Update the persistent mic button. State derives PURELY from the audio state
   * (independent of typed text/images) so the mic→stop indicator shows even when
   * text is present. Hidden when audio is unsupported, the chat is locked / a
   * message is generating, or (while idle) the voice quota is exhausted.
   */
  updateMicButtonState() {
    if (!this.micButton) return;

    const audioSupported = this.audioService?.isSupported() ?? false;
    const audioAvailable = this.audioService?.canRecord() ?? false;

    const micState = this.audioState === 'idle' ? 'mic' : this.audioState;
    const showMic = audioSupported
      && !this.isSubmitting
      && !this._isLocked()
      && (audioAvailable || this.audioState !== 'idle');

    this.micButton.hidden = !showMic;
    this.micButton.setAttribute('data-mic-state', micState);
    this.micButton.disabled = this.audioState === 'processing';

    const ariaLabel = micState === 'recording'
      ? 'Stop recording'
      : micState === 'processing'
        ? 'Transcribing audio'
        : 'Record voice message';
    this.micButton.setAttribute('aria-label', ariaLabel);
  }

  /**
   * Update the conditional submit button. Visible when there is content to send,
   * a recording in progress (submit-during-recording), or a generation to stop.
   */
  updateSubmitButtonState() {
    if (this.submitButton) {
      const hasContent = this.getValue().trim().length > 0 || this.attachedImages.length > 0;
      const audioSupported = this.audioService?.isSupported() ?? false;

      let visible;
      let inputState = 'send';
      let ariaLabel = 'Send message';
      let disabled = false;

      if (this.isSubmitting) {
        // Stop-generation — regardless of text/audio.
        visible = true;
        inputState = 'submitting';
        ariaLabel = 'Stop generation';
      } else if (this.audioState === 'recording') {
        // Submit-during-recording.
        visible = true;
      } else if (this.audioState === 'processing') {
        // Transcribing — the in-input indicator owns the input; no submit action.
        visible = false;
      } else if (hasContent) {
        visible = true;
      } else if (!audioSupported) {
        // No content and no mic button to fall back on — show a disabled send so
        // the bar isn't actionless (parity with the pre-redesign no-audio state).
        visible = true;
        disabled = true;
      } else {
        // Empty + idle + audio available — the mic button is the action.
        visible = false;
      }

      this.submitButton.hidden = !visible;
      this.submitButton.setAttribute('data-input-state', inputState);
      this.submitButton.setAttribute('aria-label', ariaLabel);
      this.submitButton.disabled = disabled;

      if (this.isSubmitting) {
        this.submitButton.setAttribute('data-submitting', 'true');
      } else {
        this.submitButton.removeAttribute('data-submitting');
      }
    }

    // Hard-disable the textarea while chat is locked (a real LLM request or a
    // programmatic stream/animation is in flight) OR while recording/transcribing
    // (so concurrent typing can't race the transcript append). This blocks every
    // input vector — typing, paste, drag-drop, IME, dictation — in one place.
    if (this.chatInput) {
      this.chatInput.disabled = this._isLocked()
        || this.audioState === 'recording'
        || this.audioState === 'processing';
    }
  }

  // ============================================================
  // RECORDING WAVEFORM UI (lifecycle owned here; levels read from the service)
  // ============================================================

  /**
   * Enter the recording visual: collapse to one row, hide the textarea (value
   * preserved), reveal the waveform, and start the rAF level loop.
   * @private
   */
  _startWaveform() {
    if (this.chatInput) this.chatInput.hidden = true;
    this.resetInputHeight();

    // Start flat: clear any leftover history from a prior recording.
    this._waveHistory.fill(0);
    this._lastWaveShift = 0;

    if (this.waveformEl) {
      this.waveformEl.classList.remove('chat-input__waveform--processing');
      if (this.waveformLabel) this.waveformLabel.textContent = '';
      this.waveformEl.hidden = false;
    }
    this._announce('Recording');
    this._waveformLoop();
  }

  /**
   * Generate the waveform bars (once) into the empty `.chat-input__waveform-bars`
   * container and cache the resulting node list. Idempotent — safe to call again.
   * @private
   */
  _buildWaveformBars() {
    const container = this.waveformEl
      ? this.waveformEl.querySelector('.chat-input__waveform-bars')
      : null;
    if (!container) {
      this.waveformBars = [];
      return;
    }
    if (container.childElementCount === 0) {
      const frag = document.createDocumentFragment();
      for (let i = 0; i < WAVEFORM_BAR_COUNT; i++) {
        const span = document.createElement('span');
        span.className = 'chat-input__waveform-bar';
        frag.appendChild(span);
      }
      container.appendChild(frag);
    }
    this.waveformBars = Array.from(
      container.querySelectorAll('.chat-input__waveform-bar')
    );
  }

  /**
   * rAF loop driving the waveform. The newest amplitude is written to the CENTER of
   * the history buffer every frame; on the WAVEFORM_SHIFT_MS cadence the buffer
   * shifts one step outward (older samples travel toward the edges). Rendering
   * mirrors the buffer left/right and tapers height to zero at the edges, so a loud
   * moment blooms at the center and ripples symmetrically outward.
   * @param {number} [now] - rAF timestamp (ms).
   * @private
   */
  _waveformLoop(now = 0) {
    if (!this.audioService || this.audioState !== 'recording') return;

    // Raw mic RMS is low for typical speech; apply gain + a compressive curve so
    // quiet input still produces visible waves (loud input saturates at 1).
    const raw = this.audioService.getAmplitude();
    const amp = Math.min(1, Math.pow(raw * WAVEFORM_GAIN, WAVEFORM_CURVE));
    const h = this._waveHistory;
    if (now - this._lastWaveShift >= WAVEFORM_SHIFT_MS) {
      this._lastWaveShift = now;
      for (let i = h.length - 1; i > 0; i--) h[i] = h[i - 1];
    }
    h[0] = amp; // center bar always tracks the live amplitude

    const half = h.length; // WAVEFORM_BAR_COUNT / 2
    for (let d = 0; d < half; d++) {
      // Envelope: 1 at center → 0 at WAVEFORM_EDGE_FADE of the way out, then flat.
      // The wave dies before the edges, so the outer bars never move.
      const t = Math.min(1, d / (half - 1) / WAVEFORM_EDGE_FADE);
      const env = Math.cos(t * (Math.PI / 2));
      const v = h[d] * env;
      const scale = (WAVEFORM_MIN_SCALE + v * (1 - WAVEFORM_MIN_SCALE)).toFixed(3);
      const right = this.waveformBars[half + d];
      const left = this.waveformBars[half - 1 - d];
      if (right) right.style.transform = `scaleY(${scale})`;
      if (left) left.style.transform = `scaleY(${scale})`;
    }

    if (typeof requestAnimationFrame === 'function') {
      this._waveformRaf = requestAnimationFrame((t) => this._waveformLoop(t));
    }
  }

  /**
   * Enter the transcribing visual: stop the loop, reset the bars to their resting
   * height, dim them, and show the centered "Transcribing…" label. Textarea stays
   * hidden.
   * @private
   */
  _freezeWaveform() {
    this._stopWaveformLoop();
    if (this.waveformEl) {
      this.waveformEl.classList.add('chat-input__waveform--processing');
      // Clear the per-frame inline scaleY so the bars fall back to the CSS resting
      // height — a flat gray backdrop behind the label, not frozen mid-wave.
      for (const bar of this.waveformBars) bar.style.transform = '';
      if (this.waveformLabel) this.waveformLabel.textContent = 'Transcribing…';
      this.waveformEl.hidden = false;
    }
    this._announce('Transcribing');
  }

  /**
   * Return to the idle text input: tear down the loop, hide the waveform, restore
   * the textarea.
   * @private
   */
  _stopWaveform() {
    this._stopWaveformLoop();
    if (this.waveformEl) {
      this.waveformEl.hidden = true;
      this.waveformEl.classList.remove('chat-input__waveform--processing');
      if (this.waveformLabel) this.waveformLabel.textContent = '';
    }
    if (this.chatInput) this.chatInput.hidden = false;
  }

  /**
   * Cancel the waveform rAF loop.
   * @private
   */
  _stopWaveformLoop() {
    if (this._waveformRaf && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this._waveformRaf);
    }
    this._waveformRaf = null;
  }

  /**
   * Announce a recording/transcribing/inserted state to the ARIA live region.
   * @param {string} message
   * @private
   */
  _announce(message) {
    if (this.srStatus) this.srStatus.textContent = message;
  }

  // ============================================================
  // IMAGE ATTACHMENT METHODS
  // ============================================================

  /**
   * Create hidden file input for image selection
   * @returns {HTMLInputElement}
   * @private
   */
  _createFileInput() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = pickerAcceptAttr();
    input.multiple = true;
    input.style.display = 'none';
    input.addEventListener('change', (e) => this._handleFileSelect(e));
    document.body.appendChild(input);
    return input;
  }

  /**
   * Handle file selection from file picker
   * @param {Event} e - Change event from file input
   * @private
   */
  async _handleFileSelect(e) {
    const files = Array.from(e.target.files);

    try {
      await this._enqueueImageFiles(files);
    } finally {
      // Reset the input that actually fired (file picker or mobile camera) so
      // selecting the same image again still emits a change event.
      e.target.value = '';
    }
  }

  /**
   * Handle image files pasted into the composer.
   * @param {ClipboardEvent} e - Paste event from the chat textarea
   * @private
   */
  _handlePaste(e) {
    if (!this._canAcceptAttachments()) return;

    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const isCandidate = (file) => this._isPasteImageCandidate(file);
    const itemFiles = Array.from(clipboardData.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(isCandidate);
    const imageFiles = itemFiles.length > 0
      ? itemFiles
      : Array.from(clipboardData.files || []).filter(isCandidate);

    if (imageFiles.length === 0) return;

    // A mixed image/text clipboard represents one pasted attachment action.
    // Suppress companion HTML/text (often an image URL) once images are accepted.
    e.preventDefault();
    void this._enqueueImageFiles(imageFiles, { fallbackPrefix: 'pasted-image' });
  }

  /**
   * Serialize image ingestion so overlapping picker/paste events cannot reserve
   * the same remaining attachment slots.
   * @param {File[]} files - Image files to validate and add
   * @param {Object} [options] - Image ingestion options
   * @param {string} [options.fallbackPrefix='image'] - Prefix when a file has no name
   * @returns {Promise<void>}
   * @private
   */
  _enqueueImageFiles(files, options = {}) {
    // Counted here rather than inside the queued callback so the pending state
    // is visible immediately — a submit landing between enqueue and execution
    // must still see work outstanding.
    this._pendingIngestions += 1;

    const ingest = () => this._handleImageFiles(files, options);
    this._imageIngestionQueue = this._imageIngestionQueue
      .then(ingest, ingest)
      .catch((error) => {
        console.error('[Input] Image attachment ingestion failed:', error);
        window.notificationManager?.showError('Could not add image attachment');
      })
      // Decremented in a `finally` on every path, including rejection: a stuck
      // counter would block sending for the rest of the session.
      .finally(() => {
        this._pendingIngestions = Math.max(0, this._pendingIngestions - 1);
        this.updateButtonState();
      });
    return this._imageIngestionQueue;
  }

  /**
   * Whether ingestion started at `generation` has been invalidated.
   *
   * Attachments are cleared synchronously on submit, but an ingestion started
   * beforehand resolves afterward. Without this check it would push into the
   * cleared list and ride along on the NEXT message.
   *
   * @param {number} generation - Value captured when the work started
   * @returns {boolean} True when the result should be discarded
   * @private
   */
  _isIngestionStale(generation) {
    return generation !== this._ingestionGeneration;
  }

  /**
   * Validate and add image files from any composer attachment source.
   * @param {File[]} files - Image files to validate and add
   * @param {Object} [options] - Image ingestion options
   * @param {string} [options.fallbackPrefix='image'] - Prefix when a file has no name
   * @returns {Promise<void>}
   * @private
   */
  async _handleImageFiles(files, { fallbackPrefix = 'image' } = {}) {
    for (const file of files) {
      if (this.attachedImages.length >= this.maxImages) {
        window.notificationManager?.showError(`Maximum ${this.maxImages} images allowed`);
        break;
      }

      const filename = file.name || this._fallbackImageFilename(file.type, fallbackPrefix);
      const classification = this._classifyImageFile(file);

      // Attachment errors name the reason on the first line and the offending
      // file on the second — a filename prefixed onto one long sentence pushes
      // the reason past the fold on a 360px toast.
      if (classification === 'rejected') {
        window.notificationManager?.showError('Unsupported file type', undefined, {
          description: `${filename} — use PNG, JPEG, WebP, GIF, or HEIC`
        });
        continue;
      }

      // Checked against the ORIGINAL file, before any conversion, so converting
      // cannot quietly widen the limit.
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > this.maxImageSizeMB) {
        window.notificationManager?.showError('File too large', undefined, {
          description: `${filename} is over the ${this.maxImageSizeMB}MB limit`
        });
        continue;
      }

      try {
        const prepared = classification === 'convertible'
          ? await this._convertToSupportedType(file, filename)
          : file;
        await this._addImage(prepared, prepared.name || filename);
      } catch (error) {
        console.error('[Input] Image attachment processing failed:', error);
        if (error?.code === 'CONVERSION_UNSUPPORTED') {
          window.notificationManager?.showError(error.message, undefined, {
            description: HEIC_UNSUPPORTED_DETAIL
          });
        } else {
          window.notificationManager?.showError('Could not process image', undefined, {
            description: filename
          });
        }
      }
    }
  }

  /**
   * Wire drag-and-drop on the composer.
   *
   * `dragleave` fires when the pointer crosses into a CHILD element, so a naive
   * handler flickers the highlight off while the pointer is still inside. The
   * enter/leave counter tracks real boundary crossings instead.
   *
   * @private
   */
  _setupDragAndDrop() {
    if (!this.container) return;

    let dragDepth = 0;

    const setDragging = (active) => {
      this.container.classList.toggle('chat-input--dragover', active);
    };

    this.container.addEventListener('dragenter', (e) => {
      if (!this._isFileDrag(e)) return;
      e.preventDefault();
      dragDepth += 1;
      if (this._canAcceptAttachments()) setDragging(true);
    });

    this.container.addEventListener('dragover', (e) => {
      // Without preventDefault on dragover the browser refuses the drop.
      if (this._isFileDrag(e)) e.preventDefault();
    });

    this.container.addEventListener('dragleave', (e) => {
      if (!this._isFileDrag(e)) return;
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) setDragging(false);
    });

    this.container.addEventListener('drop', (e) => this._handleDrop(e, () => {
      dragDepth = 0;
      setDragging(false);
    }));
  }

  /**
   * Whether a drag event carries files rather than text.
   *
   * Text dragged within the textarea must keep its native behavior.
   *
   * @param {DragEvent} e - Drag event
   * @returns {boolean} True when the drag carries files
   * @private
   */
  _isFileDrag(e) {
    return Array.from(e.dataTransfer?.types || []).includes('Files');
  }

  /**
   * Ingest files dropped onto the composer.
   *
   * preventDefault runs BEFORE the eligibility gate: a refused drop must still
   * not navigate the page away to the dropped file.
   *
   * @param {DragEvent} e - Drop event
   * @param {Function} reset - Clears the drag highlight
   * @private
   */
  _handleDrop(e, reset) {
    if (!this._isFileDrag(e)) return;

    e.preventDefault();
    reset();

    if (!this._canAcceptAttachments({ notify: true })) return;

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;

    void this._enqueueImageFiles(files, { fallbackPrefix: 'dropped-image' });
  }

  /**
   * Whether the composer can accept an attachment right now.
   *
   * One predicate for every ingestion vector (paste, drop, picker, capture) so
   * a new vector cannot quietly skip a condition the others enforce.
   *
   * @param {Object} [options] - Gate options
   * @param {boolean} [options.notify=false] - Report the reason to the user
   * @returns {boolean} True when an attachment may be ingested
   * @private
   */
  _canAcceptAttachments({ notify = false } = {}) {
    if (this._isLocked()) return false;

    // Attachments are disabled wholesale in some modes (e.g. orientation).
    if (this.plusToggleDisabled) return false;

    // Mid-recording or mid-transcription the composer belongs to the audio flow.
    if (this.audioState !== 'idle') return false;

    if (this.inputDisabledOverlay && !this.inputDisabledOverlay.hidden) return false;

    if (this.attachedImages.length >= this.maxImages) {
      if (notify) {
        window.notificationManager?.showError(`Maximum ${this.maxImages} images allowed`);
      }
      return false;
    }

    return true;
  }

  /**
   * Whether a pasted file should be treated as an image attachment.
   *
   * A plain `type.startsWith('image/')` test drops files that report a blank
   * MIME type — which is exactly how `.heic` arrives from Windows Chrome, so
   * HEIC would never reach classification on the paste path at all.
   *
   * @param {File} file - Clipboard file
   * @returns {boolean} True when the file should be ingested
   * @private
   */
  _isPasteImageCandidate(file) {
    if (!file) return false;
    if (file.type?.startsWith('image/')) return true;
    return this._classifyImageFile(file) === 'convertible';
  }

  /**
   * Classify a file as directly attachable, convertible, or unusable.
   *
   * The extension fallback is not cosmetic: Windows Chrome reports an empty
   * `file.type` for `.heic`, so MIME matching alone would classify the most
   * common HEIC source as unusable.
   *
   * @param {File} file - Candidate attachment
   * @returns {'accepted'|'convertible'|'rejected'} How the file should be handled
   * @private
   */
  _classifyImageFile(file) {
    const type = file?.type || '';

    if (ACCEPTED_IMAGE_TYPES.includes(type)) return 'accepted';
    if (CONVERTIBLE_IMAGE_TYPES.includes(type)) return 'convertible';

    if (!type) {
      const extension = (file?.name || '').split('.').pop()?.toLowerCase();
      if (CONVERTIBLE_EXTENSIONS.includes(extension)) return 'convertible';
    }

    return 'rejected';
  }

  /**
   * Replace a filename's extension.
   *
   * @param {string} filename - Original filename
   * @param {string} extension - New extension, without a leading dot
   * @returns {string} Filename carrying the new extension
   * @private
   */
  _withExtension(filename, extension) {
    return `${filename.replace(/\.[^./\\]*$/, '')}.${extension}`;
  }

  /**
   * Convert a HEIC/HEIF file into a type the vision API accepts.
   *
   * Scales and encodes in a SINGLE pass: the result is already within the
   * dimension cap, so _resizeIfNeeded short-circuits and the image is never
   * re-encoded twice. Repeated lossy passes are specifically bad for the
   * dominant attachment here — screenshots of text.
   *
   * Decoding depends on an OS-provided HEIC codec (Safari 17+); browsers
   * without one throw, which is reported as an actionable message rather than
   * a generic failure. Support is detected by attempting the decode, never by
   * sniffing the user agent.
   *
   * @param {File} file - HEIC/HEIF file to convert
   * @param {string} filename - Display filename
   * @returns {Promise<File>} Converted file carrying its true type and extension
   * @private
   */
  async _convertToSupportedType(file, filename) {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch (error) {
      console.error('[Input] HEIC decode failed:', error);
      const unsupported = new Error(HEIC_UNSUPPORTED_MESSAGE);
      unsupported.code = 'CONVERSION_UNSUPPORTED';
      throw unsupported;
    }

    try {
      const ratio = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(bitmap.width * ratio));
      canvas.height = Math.max(1, Math.floor(bitmap.height * ratio));

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas is unavailable');
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => (result ? resolve(result) : reject(new Error('Image could not be converted'))),
          CONVERSION_OUTPUT_TYPE,
          IMAGE_QUALITY
        );
      });

      // The produced type, not the requested one — same contract as the resize path.
      const mimeType = blob.type || CONVERSION_OUTPUT_TYPE;
      const converted = this._withExtension(filename, extensionForType(mimeType));
      return new File([blob], converted, { type: mimeType });
    } finally {
      // Always released: an ImageBitmap holds decoded pixel data until closed.
      bitmap.close?.();
    }
  }

  /**
   * Build a stable filename for clipboard/browser image files without a name.
   * @param {string} mimeType - Image MIME type
   * @param {string} prefix - Filename prefix
   * @returns {string}
   * @private
   */
  _fallbackImageFilename(mimeType, prefix) {
    return `${prefix}.${extensionForType(mimeType)}`;
  }

  /**
   * Add an image to the attachment list
   * @param {File} file - Image file to add
   * @param {string} [filename=file.name] - Display and transport filename
   * @private
   */
  async _addImage(file, filename = file.name) {
    const id = crypto.randomUUID();
    const generation = this._ingestionGeneration;
    const preview = URL.createObjectURL(file);
    try {
      const base64 = await this._fileToBase64(file);
      const processed = await this._resizeIfNeeded(base64, file.type);

      // Re-checked at the last moment before committing: the awaits above are
      // where a submit or clear can land.
      if (this._isIngestionStale(generation)) {
        URL.revokeObjectURL(preview);
        return;
      }

      this.attachedImages.push({
        id,
        file,
        preview,
        base64: processed.base64,
        // The PROCESSED type, not file.type: resizing may re-encode (e.g. a large
        // WebP becomes JPEG), and shipping the original label would transmit
        // JPEG bytes described as WebP.
        mimeType: processed.mimeType,
        filename
      });

      this._renderAttachmentPreviews();
      this._updatePlusButtonState();
      this.updateButtonState();
    } catch (error) {
      URL.revokeObjectURL(preview);
      throw error;
    }
  }

  /**
   * Add an image already encoded for API transport without manufacturing a File.
   * @param {{mimeType:string,data:string,filename?:string}} image
   * @private
   */
  _addTransportImage({ mimeType, data, filename = 'image' }) {
    if (!mimeType || !data) return;
    this.attachedImages.push({
      id: crypto.randomUUID(),
      preview: `data:${mimeType};base64,${data}`,
      base64: data,
      mimeType,
      filename
    });
    this._renderAttachmentPreviews();
    this._updatePlusButtonState();
    this.updateButtonState();
  }

  /**
   * Convert file to base64 string (without data URL prefix)
   * @param {File} file
   * @returns {Promise<string>}
   * @private
   */
  _fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Split a data URL into its MIME type and base64 payload.
   *
   * The MIME type is read from the URL the encoder PRODUCED, never from the one
   * we requested: per the HTML spec, `toDataURL` falls back to `image/png` when
   * the requested type is unsupported. Trusting the request would silently
   * mislabel the bytes — the exact defect this returns a type to prevent.
   *
   * @param {string} dataUrl - `data:<mime>;base64,<payload>` string
   * @returns {{ base64: string, mimeType: string }} Payload and its true type
   * @private
   */
  _parseDataUrl(dataUrl) {
    const [header, payload] = dataUrl.split(',');
    const match = /^data:([^;]+)/.exec(header || '');
    return { base64: payload || '', mimeType: match?.[1] || 'image/png' };
  }

  /**
   * Downscale an image if it exceeds the maximum dimension.
   *
   * Token cost for vision models is dimensional — ceil(w/28) * ceil(h/28) visual
   * tokens — so this, not encode quality, is what controls attachment cost.
   *
   * GIFs are downscaled like anything else: the vision API uses only the first
   * frame of an animation, so preserving animation buys the model nothing, and
   * the returned MIME type keeps the result honest either way.
   *
   * @param {string} base64 - Original base64 image
   * @param {string} mimeType - Source image MIME type
   * @param {number} [maxDimension=MAX_IMAGE_DIMENSION] - Maximum width or height
   * @returns {Promise<{ base64: string, mimeType: string }>} Payload and its true type
   * @private
   */
  _resizeIfNeeded(base64, mimeType, maxDimension = MAX_IMAGE_DIMENSION) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width <= maxDimension && img.height <= maxDimension) {
          resolve({ base64, mimeType });
          return;
        }

        const ratio = Math.min(maxDimension / img.width, maxDimension / img.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(img.width * ratio);
        canvas.height = Math.floor(img.height * ratio);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas is unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // PNG stays lossless; WebP stays WebP; everything else re-encodes as JPEG.
        let requestedType = 'image/jpeg';
        if (mimeType === 'image/png') requestedType = 'image/png';
        else if (mimeType === 'image/webp') requestedType = 'image/webp';

        resolve(this._parseDataUrl(canvas.toDataURL(requestedType, IMAGE_QUALITY)));
      };
      img.onerror = () => reject(new Error('Image could not be decoded'));
      img.src = `data:${mimeType};base64,${base64}`;
    });
  }

  /**
   * Remove an attached image by ID
   * @param {string} id - Image attachment ID
   */
  removeImage(id) {
    const index = this.attachedImages.findIndex(img => img.id === id);
    if (index !== -1) {
      const image = this.attachedImages[index];
      if (image.file && image.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(image.preview);
      }
      this.attachedImages.splice(index, 1);
      this._renderAttachmentPreviews();
      this._updatePlusButtonState();
      this.updateButtonState();
    }
  }

  /**
   * Clear all attached images and revoke preview URLs
   */
  clearAttachments() {
    // Invalidate in-flight ingestion: anything still decoding belongs to the
    // turn being cleared, not the next one.
    this._ingestionGeneration += 1;

    for (const img of this.attachedImages) {
      if (img.file && img.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(img.preview);
      }
    }
    this.attachedImages = [];
    this._renderAttachmentPreviews();
    this._updatePlusButtonState();
  }

  /**
   * Render attachment preview thumbnails above the text input
   * @private
   */
  _renderAttachmentPreviews() {
    let container = this.container?.querySelector('.chat-input__attachments');

    if (this.attachedImages.length === 0) {
      container?.remove();
      return;
    }

    if (!container) {
      container = document.createElement('div');
      container.className = 'chat-input__attachments';
      // Mount as a dedicated full-width row ABOVE `.chat-input__flex`, not inside
      // the text container: that lives in grid column 2 and clips (`overflow:
      // hidden`) on a row that can't grow, so thumbnails came out squeezed with
      // the remove button cut off. As a sibling of the flex row this renders
      // identically in the single-row base and the `--expanded` two-row layout.
      const flex = this.container?.querySelector('.chat-input__flex');
      if (flex) {
        flex.parentNode.insertBefore(container, flex);
      }
    }

    container.innerHTML = '';

    for (const img of this.attachedImages) {
      const attachment = document.createElement('div');
      attachment.className = 'chat-input__attachment';
      attachment.dataset.id = img.id;

      const thumb = document.createElement('img');
      thumb.src = img.preview;
      thumb.alt = img.filename;
      thumb.className = 'chat-input__attachment-thumb';
      attachment.appendChild(thumb);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'chat-input__attachment-remove';
      removeBtn.setAttribute('aria-label', `Remove ${img.filename}`);
      removeBtn.innerHTML = '<svg class="icon" aria-hidden="true"><use href="#icon-close"></use></svg>';
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeImage(img.id);
      });
      attachment.appendChild(removeBtn);

      container.appendChild(attachment);
    }
  }

  /**
   * Update plus button disabled state when at image limit
   * @private
   */
  _updatePlusButtonState() {
    if (!this.plusButton) return;
    // Don't re-enable if feature toggle has disabled the plus button (e.g. orientation)
    if (this.plusButton.getAttribute('aria-disabled') === 'true' && this.plusToggleDisabled) return;
    const atLimit = this.attachedImages.length >= this.maxImages;
    this.plusButton.classList.toggle('chat-input__plus--disabled', atLimit);
    this.plusButton.setAttribute('aria-disabled', atLimit.toString());
  }

  /**
   * Show notification to user
   * @param {string} message - Message to display
   * @param {string} type - Notification type ('error', 'success', 'info')
   */
  showNotification(message, type = 'error') {
    // Use the app's notification manager if available
    const notificationManager = window.app?.modules?.notificationManager;
    if (notificationManager) {
      notificationManager.show(message, type);
    } else {
      console.warn(`[Input] ${type}: ${message}`);
    }
  }
}
